// Utility functions for handling recurring tasks

function isValidDate(value) {
  if (!(value instanceof Date)) return false;
  return !Number.isNaN(value.getTime());
}

function normalizeRecurrenceType(recurrence) {
  if (!recurrence) return null;
  const raw = recurrence.type || recurrence.frequency || recurrence.rule;
  return typeof raw === 'string' ? raw.toLowerCase() : null;
}

export function expandRecurringTasks(tasks, startDate, endDate) {
  const expandedTasks = [];

  const rangeStart = new Date(startDate);
  const rangeEnd = new Date(endDate);
  if (!isValidDate(rangeStart) || !isValidDate(rangeEnd) || rangeStart > rangeEnd) {
    return tasks || [];
  }

  (tasks || []).forEach(task => {
    if (!task?.recurrence) {
      expandedTasks.push(task);
      return;
    }

    const instances = generateRecurringInstances(task, rangeStart, rangeEnd);
    expandedTasks.push(...instances);
  });

  return expandedTasks;
}

function generateRecurringInstances(baseTask, startDate, endDate) {
  const instances = [];
  const recurrence = baseTask?.recurrence;

  if (!recurrence) return [baseTask];

  const type = normalizeRecurrenceType(recurrence);
  if (!['daily', 'weekly', 'monthly', 'weekday', 'yearly', 'annually'].includes(type)) {
    return [baseTask];
  }

  const taskStart = new Date(baseTask.datetime);
  const taskEndRaw = baseTask.deadline ? new Date(baseTask.deadline) : null;

  if (!isValidDate(taskStart)) {
    return [baseTask];
  }

  const safeTaskEnd = isValidDate(taskEndRaw) ? taskEndRaw : new Date(taskStart.getTime() + 60 * 60 * 1000);
  const duration = Math.max(0, safeTaskEnd.getTime() - taskStart.getTime());

  const interval = Number.isFinite(Number(recurrence.interval)) && Number(recurrence.interval) > 0
    ? Number(recurrence.interval)
    : 1;
  const maxCount = Number.isFinite(Number(recurrence.count)) && Number(recurrence.count) > 0
    ? Number(recurrence.count)
    : null;
  const untilDate = recurrence.until ? new Date(recurrence.until) : null;

  let current = new Date(taskStart);
  let generatedCount = 0;
  const hardLimit = 800;

  while (current <= endDate && generatedCount < hardLimit) {
    if (isValidDate(untilDate) && current > untilDate) break;
    if (maxCount !== null && generatedCount >= maxCount) break;

    if (current >= startDate) {
      const instanceStart = new Date(current);
      const instanceEnd = new Date(current.getTime() + duration);

      instances.push({
        ...baseTask,
        id: `${baseTask.id}-${instanceStart.getTime()}`,
        datetime: instanceStart.toISOString(),
        deadline: instanceEnd.toISOString(),
        date: instanceStart.toISOString().slice(0, 10),
        time: instanceStart.toTimeString().slice(0, 5),
        isRecurringInstance: true,
        parentTaskId: baseTask.id,
      });
    }

    generatedCount += 1;

    if (type === 'daily') {
      current.setDate(current.getDate() + interval);
      continue;
    }

    if (type === 'weekday') {
      // Advance to next Mon-Fri
      current.setDate(current.getDate() + 1);
      while (current.getDay() === 0 || current.getDay() === 6) {
        current.setDate(current.getDate() + 1);
      }
      continue;
    }

    if (type === 'yearly' || type === 'annually') {
      current.setFullYear(current.getFullYear() + interval);
      continue;
    }

    if (type === 'weekly') {
      const fallbackDay = taskStart.getDay();
      const targetDay = recurrence.day ? getDayNumber(recurrence.day) : fallbackDay;
      current = getNextWeekday(current, targetDay, interval);
      continue;
    }

    if (type === 'monthly') {
      const targetDate = Number.isFinite(Number(recurrence.date))
        ? Number(recurrence.date)
        : taskStart.getDate();
      current = getNextMonthlyDate(current, targetDate, interval);
      continue;
    }
  }

  return instances;
}

function getDayNumber(dayName) {
  const days = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
    thursday: 4, friday: 5, saturday: 6
  };
  return days[dayName.toLowerCase()] || 0;
}

function getNextWeekday(fromDate, targetDay, intervalWeeks = 1) {
  const result = new Date(fromDate);
  const currentDay = result.getDay();
  let daysToAdd = targetDay - currentDay;

  if (daysToAdd <= 0) {
    daysToAdd += 7 * intervalWeeks;
  } else {
    daysToAdd += 7 * (intervalWeeks - 1);
  }

  result.setDate(result.getDate() + daysToAdd);
  return result;
}

function getNextMonthlyDate(fromDate, targetDate, intervalMonths = 1) {
  const result = new Date(fromDate);
  result.setMonth(result.getMonth() + intervalMonths);

  // Handle cases where the target date doesn't exist in the month (e.g., 31st)
  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  const actualDate = Math.min(targetDate, lastDayOfMonth);

  result.setDate(actualDate);
  return result;
}

export function getRecurringTasksForDateRange(tasks, startDate, endDate) {
  return expandRecurringTasks(tasks, startDate, endDate);
}

export function getRecurringTasksForDate(tasks, date) {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);

  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  return expandRecurringTasks(tasks, dayStart, dayEnd);
}
